import { Box, IconButton, IconButtonProps, Tooltip } from "@chakra-ui/react";
import {
  RiArrowRightLine,
  RiCheckLine,
  RiCloseLine,
  RiDeleteBinLine,
  RiExternalLinkLine,
  RiPencilLine,
  RiSettings4Line,
  RiSideBarFill,
  RiSideBarLine,
} from "@remixicon/react";
import { AnimatePresence } from "framer-motion";
import { MouseEvent, useState } from "react";
import { Link as RouterLink, To } from "react-router";

import MotionBox from "@/common/components/MotionBox";
import { useSidebar } from "@/common/hooks/useSidebar";

type SimpleIconButtonProps = Omit<IconButtonProps, "aria-label"> & {
  label: string;
  icon: React.ReactElement;
} & (
    | {
        to: To;
        onClick?: undefined;
      }
    | {
        to?: undefined;
        onClick: (event: MouseEvent<HTMLButtonElement>) => void;
      }
  );

export function SimpleIconButton({
  to,
  onClick,
  label,
  icon,
  isDisabled,
  ...buttonProps
}: SimpleIconButtonProps) {
  return (
    <Tooltip isDisabled={isDisabled} label={label} openDelay={300} hasArrow>
      <IconButton
        aria-label={label}
        as={to && !isDisabled ? RouterLink : undefined}
        icon={icon}
        isDisabled={isDisabled}
        size="xs"
        to={to ? to : ""}
        variant="ghost"
        onClick={onClick}
        {...buttonProps}
      />
    </Tooltip>
  );
}

type PredefinedIconButtonProps = Omit<SimpleIconButtonProps, "icon"> & {
  iconSize?: string | number;
};

export function HrefIconButton({
  iconSize = 16,
  ...buttonProps
}: PredefinedIconButtonProps) {
  return (
    <SimpleIconButton
      color="dimmed"
      {...(buttonProps as SimpleIconButtonProps)}
      icon={<RiExternalLinkLine size={iconSize} />}
    />
  );
}

export function GoToIconButton({
  iconSize = 18,
  ...buttonProps
}: PredefinedIconButtonProps) {
  return (
    <SimpleIconButton
      {...(buttonProps as SimpleIconButtonProps)}
      icon={<RiArrowRightLine size={iconSize} />}
    />
  );
}

export function ConfigureIconButton({
  iconSize = 18,
  ...buttonProps
}: PredefinedIconButtonProps) {
  return (
    <SimpleIconButton
      {...(buttonProps as SimpleIconButtonProps)}
      icon={<RiSettings4Line size={iconSize} />}
    />
  );
}

export function EditIconButton({
  iconSize = 16,
  ...buttonProps
}: PredefinedIconButtonProps) {
  return (
    <SimpleIconButton
      {...(buttonProps as SimpleIconButtonProps)}
      icon={<RiPencilLine size={iconSize} />}
    />
  );
}

export function CloseIconButton({
  iconSize = 16,
  ...buttonProps
}: PredefinedIconButtonProps) {
  return (
    <SimpleIconButton
      {...(buttonProps as SimpleIconButtonProps)}
      icon={<RiCloseLine size={iconSize} />}
    />
  );
}

export function DeleteIconButton({
  iconSize = 16,
  ...buttonProps
}: PredefinedIconButtonProps) {
  return (
    <SimpleIconButton
      {...(buttonProps as SimpleIconButtonProps)}
      icon={<RiDeleteBinLine size={iconSize} />}
    />
  );
}

const AnimatedDeleteCheckIcon = ({ check }: { check: boolean }) => {
  return (
    <AnimatePresence initial={false} mode="wait">
      {check ? (
        <MotionBox
          key="check"
          animate={{ scale: 1 }}
          exit={{ scale: 0.7 }}
          initial={{ scale: 0.7 }}
          transition={{ duration: "0.05" }}
        >
          <RiCheckLine size={16} />
        </MotionBox>
      ) : (
        <MotionBox
          key="delete"
          animate={{ scale: 1 }}
          exit={{ scale: 0.7 }}
          initial={{ scale: 0.7 }}
          transition={{ duration: "0.05" }}
        >
          <RiDeleteBinLine size={16} />
        </MotionBox>
      )}
    </AnimatePresence>
  );
};

export function DeleteConfirmIconButton({
  onClick,
  label,
  confirmLabel = "Click again to delete",
  ...buttonProps
}: {
  onClick: () => void;
  label: string;
  confirmLabel?: string;
} & Omit<IconButtonProps, "aria-label">) {
  const [confirming, setConfirming] = useState(false);
  return (
    <Tooltip
      label={confirming ? confirmLabel : undefined}
      placement="top"
      hasArrow
    >
      <IconButton
        aria-label={confirming ? confirmLabel : label}
        icon={<AnimatedDeleteCheckIcon check={confirming} />}
        size="xs"
        variant="ghost"
        onClick={(e) => {
          if (confirming) {
            onClick(e);
          } else {
            setConfirming(true);
          }
        }}
        onMouseLeave={() => setConfirming(false)}
        {...buttonProps}
      />
    </Tooltip>
  );
}

export function SidebarToggleButton({
  sidebarKey,
  iconSize = 16,
  ...buttonProps
}: {
  sidebarKey?: string;
  iconSize?: string | number;
} & Omit<IconButtonProps, "aria-label" | "icon" | "onClick">) {
  const { isVisible, toggle } = useSidebar(sidebarKey);

  return (
    <IconButton
      aria-label="Toggle sidebar"
      color="dimmed"
      icon={
        <Box transform="rotate(180deg)">
          {isVisible ? (
            <RiSideBarFill size={iconSize} />
          ) : (
            <RiSideBarLine size={iconSize} />
          )}
        </Box>
      }
      size="xs"
      variant="ghost"
      onClick={toggle}
      {...buttonProps}
    />
  );
}
